import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { SITE } from "@/constants/site";

const SERVICES = [
  "Residential advisory — apartments, penthouses and villas",
  "Commercial advisory — offices, retail and showrooms",
  "Luxury property search with private viewings",
  "Resale and seller representation with verified titles",
  "Rental advisory for tenants and landlords",
  "Investment guidance across Ahmedabad and GIFT City",
];

const AREAS = [
  "Adani Shantigram & Vaishnodevi",
  "SG Highway corridor",
  "Gota, Chandkheda & Motera",
  "Science City & Sola",
  "Sindhu Bhavan Road & Bodakdev",
  "GIFT City & Gandhinagar corridor",
];

export default function AboutPage() {
  useEffect(() => {
    document.title = "About VR Ahmedabad Realtors | Luxury Real Estate Advisory, Ahmedabad";
    const meta = document.querySelector("meta[name='description']");
    const prev = meta?.content;
    if (meta)
      meta.content =
        "About VR Ahmedabad Realtors — a boutique luxury real estate advisory in Ahmedabad specialising in Adani Shantigram: residential, commercial, resale, rental and investment services.";
    return () => {
      if (meta && prev) meta.content = prev;
    };
  }, []);

  return (
    <main data-testid="about-page" className="bg-cream font-sans text-slate-800 antialiased">
      <section className="grain bg-forest py-24">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
          <Link to="/" className="flex w-fit items-center gap-3">
            <img src="/vr-logo.jpeg" alt="VR Ahmedabad Realtors logo" width="48" height="48" className="h-12 w-12 rounded-sm object-cover ring-1 ring-gold/50" />
            <span className="font-serif text-lg text-white">VR Ahmedabad Realtors</span>
          </Link>
          <h1 className="mt-10 max-w-3xl font-serif text-4xl tracking-tighter text-white sm:text-5xl">
            About VR Ahmedabad Realtors
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75">
            A boutique real estate advisory based in Ahmedabad, Gujarat — built around one idea: buyers and sellers
            deserve an advisor who works for them, not for the listing.
          </p>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="md:col-span-7">
            <h2 className="font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">Who we are</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              VR Ahmedabad Realtors is a luxury residential and commercial real estate advisory headquartered in
              Ahmedabad. Our deepest expertise is Adani Shantigram — the 600-acre township on SG Highway where we
              handle new bookings, verified resale units and rentals across every cluster, every day.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Beyond the township, we advise across Ahmedabad's premium corridors — Sindhu Bhavan Road, Bodakdev,
              Science City — and the GIFT City growth belt. Every engagement is brief-first: we listen, shortlist
              verified options, negotiate on your side and stay accountable through registration.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              We share our credentials and registration details transparently during engagement — ask us anytime.
            </p>
            <h2 className="mt-12 font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl">What we do</h2>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-forest" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-lg border border-black/5 bg-cream p-8">
              <h2 className="font-serif text-xl text-slate-900">Areas we serve</h2>
              <ul className="mt-5 space-y-2.5">
                {AREAS.map((a) => (
                  <li key={a} className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="h-1 w-1 rounded-full bg-gold" />
                    {a}
                  </li>
                ))}
              </ul>
              <div className="mt-8 border-t border-black/5 pt-6">
                <p className="text-xs uppercase tracking-widest text-slate-400">Talk to us</p>
                <a href={`tel:${SITE.phoneRaw}`} data-testid="about-phone-link" className="mt-2 block font-serif text-xl text-forest">
                  {SITE.phoneDisplay}
                </a>
                <a href={`mailto:${SITE.email}`} className="mt-1 block text-sm text-slate-600 hover:text-forest">
                  {SITE.email}
                </a>
                <Link
                  to="/contact"
                  data-testid="about-contact-cta"
                  className="mt-6 block rounded-sm bg-forest px-6 py-3.5 text-center text-sm font-bold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-forest-hover"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
